/**
 * Mock data for SRE Dashboard — used as fallback when the backend proxy is unavailable.
 *
 * Provides realistic demo data aligned with the SkyTech Corp customer scenario.
 * Includes AWS, Azure, and Databricks incidents matching the demo sessions.
 */
import type {
  Incident,
  SLODefinition,
  Postmortem,
  Checklist,
  KaizenItem,
  PlatformRole,
  UserRoleAssignment,
  AuditLog,
  ResourceExclusion,
  IncidentId,
} from '../types';

// ── Helper: cast string to branded type ────────────────────────
function id<T>(value: string): T {
  return value as unknown as T;
}

// ── Incidents ──────────────────────────────────────────────────

export const MOCK_INCIDENTS: readonly Incident[] = [
  {
    id: id<IncidentId>('inc-001'),
    incidentNumber: 'INC-2026-0001',
    title: 'CPUUtilization High - payment-service',
    description: 'payment-service の CPU 使用率が 95% を超過。Grafana アラートにより自動検出。',
    severity: 'critical',
    status: 'investigating',
    provider: 'AWS',
    affectedResources: ['i-0abc123def456', 'payment-service-prod'],
    source: 'webhook',
    assignee: '山田太郎',
    tags: ['production', 'payment'],
    timeline: [
      { id: 'tl-001', type: 'alert_fired', description: 'Grafana: CPUUtilization > 90% が5分間継続', actor: 'Grafana', timestamp: '2026-02-21T09:00:00Z' },
      { id: 'tl-002', type: 'ticket_created', description: 'インシデントチケット自動作成', actor: 'System', timestamp: '2026-02-21T09:00:05Z' },
      { id: 'tl-003', type: 'runbook_started', description: 'Runbook cpu-high-response を自動実行開始', actor: 'SuperAgent', timestamp: '2026-02-21T09:00:10Z' },
      { id: 'tl-004', type: 'status_changed', description: 'ステータスを investigating に変更', actor: '山田太郎', timestamp: '2026-02-21T09:01:00Z' },
    ],
    runbookId: 'rb-cpu-high',
    runbookExecutionId: 'exec-001',
    createdAt: '2026-02-21T09:00:00Z',
    updatedAt: '2026-02-21T09:01:00Z',
    resolvedAt: null,
  },
  {
    id: id<IncidentId>('inc-002'),
    incidentNumber: 'INC-2026-0002',
    title: 'ELB HealthCheck Failure - web-frontend',
    description: 'web-frontend の ELB ヘルスチェックが失敗。複数ターゲットが unhealthy 状態。',
    severity: 'high',
    status: 'open',
    provider: 'AWS',
    affectedResources: ['alb-web-frontend-prod', 'tg-web-frontend'],
    source: 'webhook',
    assignee: null,
    tags: ['production', 'web-frontend'],
    timeline: [
      { id: 'tl-005', type: 'alert_fired', description: 'Grafana: ELB HealthyHostCount < 2 が3分間継続', actor: 'Grafana', timestamp: '2026-02-21T10:30:00Z' },
      { id: 'tl-006', type: 'ticket_created', description: 'インシデントチケット自動作成', actor: 'System', timestamp: '2026-02-21T10:30:05Z' },
    ],
    runbookId: null,
    runbookExecutionId: null,
    createdAt: '2026-02-21T10:30:00Z',
    updatedAt: '2026-02-21T10:30:05Z',
    resolvedAt: null,
  },
  {
    id: id<IncidentId>('inc-003'),
    incidentNumber: 'INC-2026-0003',
    title: 'DiskUtilization Warning - batch-processor',
    description: 'batch-processor のディスク使用率が 80% を超過。ログローテーション推奨。',
    severity: 'medium',
    status: 'resolved',
    provider: 'AWS',
    affectedResources: ['i-0xyz789ghi012', 'batch-processor-prod'],
    source: 'webhook',
    assignee: '鈴木花子',
    tags: ['production', 'batch-processor'],
    timeline: [
      { id: 'tl-007', type: 'alert_fired', description: 'Grafana: DiskUtilization > 80%', actor: 'Grafana', timestamp: '2026-02-21T08:00:00Z' },
      { id: 'tl-008', type: 'runbook_completed', description: 'Runbook disk-cleanup 完了: 15GB削除', actor: 'SuperAgent', timestamp: '2026-02-21T08:05:00Z' },
      { id: 'tl-009', type: 'status_changed', description: 'ステータスを resolved に変更', actor: '鈴木花子', timestamp: '2026-02-21T08:10:00Z' },
    ],
    runbookId: 'rb-disk-cleanup',
    runbookExecutionId: 'exec-002',
    createdAt: '2026-02-21T08:00:00Z',
    updatedAt: '2026-02-21T08:10:00Z',
    resolvedAt: '2026-02-21T08:10:00Z',
  },
  {
    id: id<IncidentId>('inc-004'),
    incidentNumber: 'INC-2026-0004',
    title: 'Databricks ETL ジョブ OOM 障害 - ml-feature-generation',
    description: 'ML 特徴量生成ジョブが OOM で失敗。キャンペーン施策によるデータ量 3 倍増が原因。下流 3 テーブルの Freshness SLO 違反。',
    severity: 'high',
    status: 'mitigating',
    provider: 'Databricks',
    affectedResources: ['cluster-etl-prod-01', 'job-ml-feature-generation', 'churn_prediction_features', 'customer_360'],
    source: 'webhook',
    assignee: '山田太郎',
    tags: ['production', 'databricks', 'etl', 'oom'],
    timeline: [
      { id: 'tl-010', type: 'alert_fired', description: 'Databricks Job Alert: ml-feature-generation FAILED (OOM)', actor: 'Databricks', timestamp: '2026-02-22T06:45:00Z' },
      { id: 'tl-011', type: 'runbook_started', description: 'OOM 原因分析 Runbook 実行開始', actor: 'SuperAgent', timestamp: '2026-02-22T06:45:30Z' },
      { id: 'tl-012', type: 'status_changed', description: 'Spark 設定チューニング + クラスターリサイズ実施', actor: 'Performance Agent', timestamp: '2026-02-22T07:00:00Z' },
    ],
    runbookId: 'rb-databricks-oom',
    runbookExecutionId: 'exec-003',
    createdAt: '2026-02-22T06:45:00Z',
    updatedAt: '2026-02-22T07:00:00Z',
    resolvedAt: null,
  },
  {
    id: id<IncidentId>('inc-005'),
    incidentNumber: 'INC-2026-0005',
    title: 'Databricks DBU コスト異常 — 開発クラスター 72h アイドル',
    description: 'ALL_PURPOSE_COMPUTE の DBU 消費が前週比 280% 増。開発クラスターの auto_termination 無効で 72 時間アイドル稼働。',
    severity: 'high',
    status: 'resolved',
    provider: 'Databricks',
    affectedResources: ['cluster-dev-interactive-01'],
    source: 'anomaly_detection',
    assignee: 'Cost Agent',
    tags: ['databricks', 'cost', 'dbu', 'idle-cluster'],
    timeline: [
      { id: 'tl-013', type: 'alert_fired', description: 'DBU 消費量アラート: 前週比 280% 増', actor: 'Cost Anomaly Detection', timestamp: '2026-02-22T09:00:00Z' },
      { id: 'tl-014', type: 'ticket_created', description: 'AI 起票文自動生成 — チケット DBX-2026-0042', actor: 'Cost Agent', timestamp: '2026-02-22T09:05:00Z' },
      { id: 'tl-015', type: 'status_changed', description: 'クラスター停止 + ガバナンスポリシー適用', actor: 'Cost Agent', timestamp: '2026-02-22T09:15:00Z' },
    ],
    runbookId: null,
    runbookExecutionId: null,
    createdAt: '2026-02-22T09:00:00Z',
    updatedAt: '2026-02-22T09:15:00Z',
    resolvedAt: '2026-02-22T09:15:00Z',
  },
  {
    id: id<IncidentId>('inc-006'),
    incidentNumber: 'INC-2026-0006',
    title: 'Databricks データ品質ゲート失敗 — customer_master NULL 率超過',
    description: 'Oracle 連携テーブル customer_master の品質ゲート失敗。email NULL 率 12.3% (閾値 5%)。下流 5 テーブルへの影響あり。',
    severity: 'high',
    status: 'investigating',
    provider: 'Databricks',
    affectedResources: ['customer_master', 'customer_360', 'churn_prediction_features', 'marketing_segments'],
    source: 'webhook',
    assignee: 'Log Agent',
    tags: ['databricks', 'data-quality', 'unity-catalog', 'oracle'],
    timeline: [
      { id: 'tl-016', type: 'alert_fired', description: '品質ゲート失敗: customer_master — email NULL 率 12.3% > 閾値 5%', actor: 'DLT Pipeline', timestamp: '2026-02-22T11:30:00Z' },
      { id: 'tl-017', type: 'runbook_started', description: '品質ゲート失敗対応 Runbook 開始', actor: 'SuperAgent', timestamp: '2026-02-22T11:30:30Z' },
    ],
    runbookId: 'rb-data-quality',
    runbookExecutionId: 'exec-004',
    createdAt: '2026-02-22T11:30:00Z',
    updatedAt: '2026-02-22T11:30:30Z',
    resolvedAt: null,
  },
  {
    id: id<IncidentId>('inc-007'),
    incidentNumber: 'INC-2026-0007',
    title: 'GPU インスタンス停止忘れ — EC2 コスト 134% 増',
    description: 'p3.8xlarge × 4 台が 48 時間アイドル。学習完了後の停止忘れにより ¥510,000/日 の超過。',
    severity: 'high',
    status: 'resolved',
    provider: 'AWS',
    affectedResources: ['i-gpu-train-01', 'i-gpu-train-02', 'i-gpu-train-03', 'i-gpu-train-04'],
    source: 'anomaly_detection',
    assignee: 'Cost Agent',
    tags: ['aws', 'ec2', 'gpu', 'cost-anomaly'],
    timeline: [
      { id: 'tl-018', type: 'alert_fired', description: 'Cost Anomaly Detection: EC2 利用料金前日比 134% 増', actor: 'AWS', timestamp: '2026-02-20T14:00:00Z' },
      { id: 'tl-019', type: 'ticket_created', description: 'AI 起票文自動生成 — チケット COST-2026-0215', actor: 'Cost Agent', timestamp: '2026-02-20T14:05:00Z' },
      { id: 'tl-020', type: 'status_changed', description: 'GPU インスタンス停止完了', actor: 'Cost Agent', timestamp: '2026-02-20T14:10:00Z' },
    ],
    runbookId: null,
    runbookExecutionId: null,
    createdAt: '2026-02-20T14:00:00Z',
    updatedAt: '2026-02-20T14:10:00Z',
    resolvedAt: '2026-02-20T14:10:00Z',
  },
];

// ── SLO Definitions ────────────────────────────────────────────

export const MOCK_SLOS: readonly SLODefinition[] = [
  {
    id: id('slo-001'),
    serviceName: 'payment-service',
    sliType: 'availability',
    target: 99.95,
    window: '30d',
    description: '決済サービスの可用性 SLO',
    createdAt: '2026-01-01T00:00:00Z',
    updatedAt: '2026-02-20T00:00:00Z',
  },
  {
    id: id('slo-002'),
    serviceName: 'web-frontend',
    sliType: 'latency',
    target: 99.0,
    window: '30d',
    description: 'フロントエンド API p99 レイテンシ < 500ms',
    createdAt: '2026-01-01T00:00:00Z',
    updatedAt: '2026-02-20T00:00:00Z',
  },
  {
    id: id('slo-003'),
    serviceName: 'batch-processor',
    sliType: 'error_rate',
    target: 99.9,
    window: '7d',
    description: 'バッチ処理のエラー率 SLO',
    createdAt: '2026-01-15T00:00:00Z',
    updatedAt: '2026-02-20T00:00:00Z',
  },
  {
    id: id('slo-004'),
    serviceName: 'databricks-etl-pipeline',
    sliType: 'availability',
    target: 99.5,
    window: '30d',
    description: 'Databricks 日次 ETL パイプラインの Data Freshness SLO',
    createdAt: '2026-01-15T00:00:00Z',
    updatedAt: '2026-02-22T00:00:00Z',
  },
];

// ── Postmortems ────────────────────────────────────────────────

export const MOCK_POSTMORTEMS: readonly Postmortem[] = [
  {
    id: id('pm-001'),
    incidentId: id<IncidentId>('inc-003'),
    title: 'batch-processor ディスク使用率超過',
    summary: 'ログローテーション設定の不備により、バッチプロセッサーのディスクが逼迫。自動 Runbook で復旧。',
    timeline: '08:00 アラート発火 → 08:00 Runbook 自動実行 → 08:05 15GB 削除完了 → 08:10 復旧確認',
    impact: 'バッチ処理の一部遅延（10分間）',
    fiveWhys: [
      { level: 1, question: 'なぜディスクが逼迫したか？', answer: 'ログファイルが膨大に蓄積されていた', evidence: 'du -sh /var/log: 45GB', actionItem: null },
      { level: 2, question: 'なぜログが蓄積されたか？', answer: 'logrotate の maxage 設定が 90 日だった', evidence: 'logrotate.conf', actionItem: 'maxage を 30 日に変更' },
    ],
    rootCause: 'logrotate の maxage 設定が 90 日と長すぎ、ログが蓄積された',
    actionItems: [
      { id: id('ai-001'), description: 'logrotate maxage を 30 日に変更', owner: '鈴木花子', dueDate: '2026-03-01', status: 'completed', priority: 'medium', createdAt: '2026-02-21T10:00:00Z', updatedAt: '2026-02-21T10:00:00Z' },
    ],
    contributors: ['鈴木花子', '山田太郎'],
    status: 'published',
    createdAt: '2026-02-21T10:00:00Z',
    updatedAt: '2026-02-21T15:00:00Z',
    publishedAt: '2026-02-21T15:00:00Z',
    deadline: '2026-03-07T00:00:00Z',
  },
];

// ── Checklist ──────────────────────────────────────────────────

export const MOCK_CHECKLIST: Checklist = {
  id: id('cl-001'),
  date: new Date().toISOString().slice(0, 10),
  operator: '山田太郎',
  items: [
    { id: 'ci-01', category: 'overnight_alerts', label: '夜間アラート確認（Grafana / PagerDuty）', checked: true, note: '2 件のアラートを確認済み' },
    { id: 'ci-02', category: 'open_incidents', label: 'オープンインシデント確認', checked: true, note: 'INC-2026-0001, INC-2026-0004 対応中' },
    { id: 'ci-03', category: 'scheduled_work', label: '本日の予定作業確認', checked: false, note: '' },
    { id: 'ci-04', category: 'resource_usage', label: 'リソース使用状況チェック（CPU / Memory / Disk）', checked: false, note: '' },
    { id: 'ci-05', category: 'kaizen_followup', label: 'カイゼン項目フォローアップ', checked: false, note: '' },
  ],
  completedAt: null,
  createdAt: `${new Date().toISOString().slice(0, 10)}T09:00:00Z`,
};

// ── Kaizen Items ───────────────────────────────────────────────

export const MOCK_KAIZEN_ITEMS: readonly KaizenItem[] = [
  {
    id: id('kz-001'),
    title: 'GPU 自動停止ルール導入',
    description: 'GPU インスタンスの停止忘れを防止するため、8 時間アイドルで自動停止する Lambda を導入',
    category: 'cost_optimization',
    status: 'in_progress',
    impact: 'high',
    effort: 'medium',
    isToil: false,
    proposer: 'Cost Agent',
    createdAt: '2026-02-20T15:00:00Z',
    updatedAt: '2026-02-22T10:00:00Z',
    completedAt: null,
  },
  {
    id: id('kz-002'),
    title: 'Databricks auto_termination 強制ポリシー',
    description: '全開発クラスターに auto_termination_minutes >= 30 を強制するクラスターポリシーを適用',
    category: 'cost_optimization',
    status: 'proposed',
    impact: 'high',
    effort: 'low',
    isToil: false,
    proposer: 'Cost Agent',
    createdAt: '2026-02-22T10:00:00Z',
    updatedAt: '2026-02-22T10:00:00Z',
    completedAt: null,
  },
  {
    id: id('kz-003'),
    title: 'ログローテーション設定の標準化',
    description: '全サーバーの logrotate maxage を 30 日に統一し、Ansible で自動適用',
    category: 'toil_reduction',
    status: 'completed',
    impact: 'medium',
    effort: 'low',
    isToil: true,
    proposer: '鈴木花子',
    createdAt: '2026-02-21T11:00:00Z',
    updatedAt: '2026-02-22T09:00:00Z',
    completedAt: '2026-02-22T09:00:00Z',
  },
  {
    id: id('kz-004'),
    title: 'Data Quality Gate の Slack 通知改善',
    description: '品質ゲート失敗時に下流影響テーブル数と SLO 違反リスクを Slack 通知に含める',
    category: 'monitoring',
    status: 'proposed',
    impact: 'medium',
    effort: 'low',
    isToil: false,
    proposer: 'Log Agent',
    createdAt: '2026-02-22T12:00:00Z',
    updatedAt: '2026-02-22T12:00:00Z',
    completedAt: null,
  },
];

// ── Governance: Roles ──────────────────────────────────────────

export const MOCK_ROLES: readonly PlatformRole[] = [
  {
    id: 'role-001',
    name: 'sre-admin',
    permissions: ['*'],
    description: 'SRE 管理者 — 全操作権限',
  },
  {
    id: 'role-002',
    name: 'sre-operator',
    permissions: ['incident:read', 'incident:update', 'runbook:execute', 'slo:read', 'daily-ops:manage', 'exclusion:manage'],
    description: 'SRE オペレーター — 運用操作権限',
  },
  {
    id: 'role-003',
    name: 'sre-viewer',
    permissions: ['incident:read', 'slo:read', 'daily-ops:read', 'report:read'],
    description: 'SRE ビューワー — 閲覧のみ',
  },
];

// ── Governance: Assignments ────────────────────────────────────

export const MOCK_ASSIGNMENTS: readonly UserRoleAssignment[] = [
  {
    id: id('asgn-001'),
    userId: 'user-yamada',
    roleName: 'sre-admin',
    assignedBy: 'system',
    assignedAt: '2026-01-01T00:00:00Z',
  },
  {
    id: id('asgn-002'),
    userId: 'user-suzuki',
    roleName: 'sre-operator',
    assignedBy: 'user-yamada',
    assignedAt: '2026-01-15T00:00:00Z',
  },
  {
    id: id('asgn-003'),
    userId: 'user-tanaka',
    roleName: 'sre-viewer',
    assignedBy: 'user-yamada',
    assignedAt: '2026-02-01T00:00:00Z',
  },
];

// ── Governance: Audit Logs ─────────────────────────────────────

export const MOCK_AUDIT_LOGS: readonly AuditLog[] = [
  {
    id: id('audit-001'),
    operator: '山田太郎',
    operationType: 'incident.status_change',
    targetResource: 'INC-2026-0001',
    timestamp: '2026-02-21T09:01:00Z',
    result: 'success',
    ipAddress: '10.0.1.100',
    details: { from: 'open', to: 'investigating' },
  },
  {
    id: id('audit-002'),
    operator: 'Cost Agent',
    operationType: 'resource.stop',
    targetResource: 'cluster-dev-interactive-01',
    timestamp: '2026-02-22T09:15:00Z',
    result: 'success',
    ipAddress: '10.0.1.200',
    details: { reason: 'DBU 超過 (BR-DATA-003)', action: 'cluster_terminate' },
  },
  {
    id: id('audit-003'),
    operator: 'Cost Agent',
    operationType: 'ticket.create',
    targetResource: 'DBX-2026-0042',
    timestamp: '2026-02-22T09:05:00Z',
    result: 'success',
    ipAddress: '10.0.1.200',
    details: { type: 'cost_anomaly', assignee: 'yamamoto' },
  },
  {
    id: id('audit-004'),
    operator: '鈴木花子',
    operationType: 'incident.status_change',
    targetResource: 'INC-2026-0003',
    timestamp: '2026-02-21T08:10:00Z',
    result: 'success',
    ipAddress: '10.0.1.101',
    details: { from: 'investigating', to: 'resolved' },
  },
];

// ── Resource Exclusions ────────────────────────────────────────

export const MOCK_EXCLUSIONS: readonly ResourceExclusion[] = [
  {
    id: id('excl-001'),
    resourceId: 'i-dev-stress-test',
    resourceName: 'dev-stress-test-instance',
    reason: 'ストレステスト用インスタンス — 意図的な高負荷のためアラート除外',
    excludedBy: '山田太郎',
    excludeUntil: '2026-03-15T00:00:00Z',
    isActive: true,
    createdAt: '2026-02-15T10:00:00Z',
    reactivatedAt: null,
    reviewReminder: '2026-03-10T09:00:00Z',
  },
  {
    id: id('excl-002'),
    resourceId: 'cluster-ml-training-large',
    resourceName: 'ML 大規模学習クラスター',
    reason: '月次 ML 学習ジョブ用 — DBU コストアラート除外',
    excludedBy: '山田太郎',
    excludeUntil: '2026-02-28T00:00:00Z',
    isActive: true,
    createdAt: '2026-02-20T09:00:00Z',
    reactivatedAt: null,
    reviewReminder: '2026-02-27T09:00:00Z',
  },
];
